'use client';

import { useState } from 'react';
import { useFirestore } from '@/firebase';
import { collection, addDoc, Timestamp } from 'firebase/firestore';
import { GoogleReCaptchaProvider, useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { useFeatureFlags } from '@/hooks/use-feature-flags';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { useToast } from '@/hooks/use-toast';
import { Loader2, Mail, Send, CheckCircle2 } from 'lucide-react';

function ContactFormContent() {
  const firestore = useFirestore();
  const { toast } = useToast();
  const { executeRecaptcha } = useGoogleReCaptcha();
  const { flags } = useFeatureFlags();
  
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  
  // Form state
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validation
    if (!name.trim() || !email.trim() || !subject.trim() || !message.trim()) {
      toast({
        variant: 'destructive',
        title: 'Validation Error',
        description: 'Please fill in all fields.',
      });
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      toast({
        variant: 'destructive',
        title: 'Invalid Email',
        description: 'Please enter a valid email address.',
      });
      return;
    }

    try {
      setLoading(true);

      // Execute reCAPTCHA
      if (!executeRecaptcha) {
        toast({
          variant: 'destructive',
          title: 'Security Check Failed',
          description: 'Please refresh the page and try again.',
        });
        setLoading(false);
        return;
      }

      const token = await executeRecaptcha('contact_form');

      // For now, we just verify the token exists
      // In production, you would verify this token server-side
      if (!token) {
        toast({
          variant: 'destructive',
          title: 'Security Check Failed',
          description: 'Please try again.',
        });
        setLoading(false);
        return;
      }

      // Officer Messages feature flag controls whether we keep a
      // Firestore record. When the feature is off, the in-app inbox
      // doesn't exist anyway — writes would just accumulate as dead
      // data — so skip it and rely entirely on email recipients.
      let messageId: string | null = null;
      if (flags.officerMessagesEnabled) {
        const docRef = await addDoc(collection(firestore, 'messages'), {
          name: name.trim(),
          email: email.trim().toLowerCase(),
          subject: subject.trim(),
          message: message.trim(),
          status: 'new',
          receiverId: null, // Explicitly set to null so admin/officer FAB picks it up
          createdAt: Timestamp.now(),
        });
        messageId = docRef.id;
      }

      // Always trigger the notify route — it consults the
      // emailContact flag server-side to decide who (if anyone) to
      // email. Non-blocking on failure so the submitter sees
      // success regardless of mail-server hiccups.
      try {
        await fetch('/api/contact/notify', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            messageId,
            senderName: name.trim(),
            senderEmail: email.trim().toLowerCase(),
            subject: subject.trim(),
            message: message.trim(),
          }),
        });
      } catch (notifyError) {
        // Log but don't fail the submission
        console.error('Failed to send notification emails:', notifyError);
      }

      setSubmitted(true);
      
      // Reset form
      setName('');
      setEmail('');
      setSubject('');
      setMessage('');

      toast({
        title: 'Message Sent',
        description: 'Thank you for contacting us. We\'ll get back to you soon!',
      });
    } catch (error) {
      console.error('Error submitting message:', error);
      toast({
        variant: 'destructive',
        title: 'Submission Failed',
        description: 'Could not send your message. Please try again.',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="flex-1 w-full max-w-2xl mx-auto p-4 md:p-8 space-y-6">
      {/* Header */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-3xl">
            <Mail className="w-8 h-8 text-primary" />
            Contact Us
          </CardTitle>
          <CardDescription>
            Have a question or want to learn more about SRAC? Send us a message and we'll get back to you soon.
          </CardDescription>
        </CardHeader>
      </Card>

      {/* Success Message */}
      {submitted && (
        <Alert className="border-green-500 bg-green-50">
          <CheckCircle2 className="h-4 w-4 text-green-600" />
          <AlertTitle className="text-green-600">Message Sent Successfully!</AlertTitle>
          <AlertDescription className="text-green-600">
            Thank you for reaching out. A club officer will review your message and respond via email soon.
          </AlertDescription>
        </Alert>
      )}

      {/* Contact Form */}
      <Card>
        <CardHeader>
          <CardTitle>Send us a Message</CardTitle>
          <CardDescription>
            Fill out the form below and we'll respond to your email address
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Name */}
            <div className="space-y-2">
              <Label htmlFor="name">Name *</Label>
              <Input
                id="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Your full name"
                required
              />
            </div>

            {/* Email */}
            <div className="space-y-2">
              <Label htmlFor="email">Email Address *</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your.email@example.com"
                required
              />
              <p className="text-sm text-muted-foreground">
                We'll use this email to respond to your message
              </p>
            </div>

            {/* Subject */}
            <div className="space-y-2">
              <Label htmlFor="subject">Subject *</Label>
              <Input
                id="subject"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                placeholder="What is your message about?"
                required
              />
            </div>

            {/* Message */}
            <div className="space-y-2">
              <Label htmlFor="message">Message *</Label>
              <Textarea
                id="message"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Tell us about your question, interest in joining, or any other inquiry..."
                rows={8}
                required
              />
            </div>

            {/* Submit Button */}
            <Button type="submit" disabled={loading} size="lg" className="w-full">
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <Send className="mr-2 h-5 w-5" />
                  Send Message
                </>
              )}
            </Button>
          </form>
        </CardContent>
      </Card>

      {/* Additional Info */}
      <Card>
        <CardContent className="pt-6">
          <h3 className="font-semibold mb-2">Other Ways to Reach Us</h3>
          <div className="space-y-2 text-sm text-muted-foreground">
            <p>• Visit us at our flying field during club events</p>
            <p>• Check the Events calendar for meeting dates</p>
            <p>• Follow us on social media for updates</p>
          </div>
        </CardContent>
      </Card>
    </main>
  );
}

export default function ContactPage() {
  // Get reCAPTCHA site key from environment variable
  // You need to add NEXT_PUBLIC_RECAPTCHA_SITE_KEY to your .env.local file
  const recaptchaSiteKey = process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY || '';

  if (!recaptchaSiteKey) {
    console.warn('reCAPTCHA site key is not configured. Please add NEXT_PUBLIC_RECAPTCHA_SITE_KEY to your environment variables.');
  }

  return (
    <GoogleReCaptchaProvider reCaptchaKey={recaptchaSiteKey}>
      <ContactFormContent />
    </GoogleReCaptchaProvider>
  );
}
